import React, { useCallback, useEffect, useState } from "react";
import { TransitionGroup } from 'react-transition-group';
import { Container, Grid, Stack, TextField, MenuItem, Box } from "@mui/material";
import Collapse from '@mui/material/Collapse';

import { IProductNavigatorNode, IProductNavigatorParentNode } from "helpers/interfaces";
import { useTranslations } from "@fenetech/translations";
import useIsMobile from "helpers/hooks/useIsMobile";
import useWait from "helpers/context/Page/useWait";
import { IPartKey } from "helpers/interfaces";
import Constants from "helpers/constants";
import ProductNavigatorNodeDisplay from "./ProductNavigatorNodeDisplay";
import ProductNavigatorAPI from "components/ProductNavigator/ProductNavigatorAPI";
import ProductNavigatorBreadCrumbs from "components/ProductNavigator/ProductNavigatorBreadCrumbs"
import ProductNavigatorPartFilter from "./ProductNavigatorPartFilters";
import useQuoteActions from "components/Quotes/useQuoteActions";
import { useSearchParams } from "react-router-dom";
import { CaseInsensitiveCompare, GetParamCaseInsensitive, isNullOrWhiteSpace } from "helpers/objects";

interface IProps {
    oKey: number;
    initialFinalNodeID: string;
    initialCallSize: string;
    partList: IPartKey[] | null;
    onCancel: () => void;
    onPartSelected: (partKey: IPartKey) => void;
    onFinalNodeChange?: (newNodeID: string | undefined) => void;
    onCallSizeChange?: (newCallSize: string) => void;
}

const ProductNavigator = ({oKey, initialFinalNodeID, initialCallSize, partList, onCancel, onPartSelected, onFinalNodeChange, onCallSizeChange} : IProps) => {
    const tm = useTranslations();
    const wait = useWait();
    const isMobile = useIsMobile();

    const [callSizeList, setCallSizeList] = useState<string[]>([]);
    const [displayHierarchy, setDisplayHierarchy] = useState<IProductNavigatorNode[] | null>(null);
    const [callSize, setCallSize] = useState<string>("");
    const [nodeMap, setNodeMap] = useState<Map<string, IProductNavigatorParentNode>>(new Map());
    const [finalNode, setFinalNode] = useState<IProductNavigatorParentNode | null>(null);
    const [displayNodes, setDisplayNodes] = useState<IProductNavigatorNode[]>([]);
    const [callSizesLoading, setCallSizesLoading] = useState<boolean>(true);
    const [parentNodesLoading, setParentNodesLoading] = useState<boolean>(true);
    const [displayHierarchyLoading, setDisplayHierarchyLoading] = useState<boolean>(true);
    const [initialLoadingStarted, setInitialLoadingStarted] = useState<boolean>(false);
    const quoteActions = useQuoteActions();
    
    const [query] = useSearchParams();
    const initialPartNo = GetParamCaseInsensitive(query, "initialPartNo");
    const initialPartNoSuffix = GetParamCaseInsensitive(query, "initialPartNoSuffix");

    useEffect(() => {
        wait.Show(callSizesLoading || parentNodesLoading || displayHierarchyLoading);
    }, [callSizesLoading, parentNodesLoading, displayHierarchyLoading, wait]);
    
    const getDisplayHierarchy = useCallback(async (callSize: string) => {
        setDisplayHierarchyLoading(true);
        return await ProductNavigatorAPI.ProductNavigator(Number(oKey), callSize, partList).then((nh) => {
            setDisplayHierarchy(nh);
            return nh;
        }).finally(() => {
            setDisplayHierarchyLoading(false);
        });
    }, [oKey, partList]);

    const changeCallSize = useCallback((callSize: string) => {
        setCallSize(callSize);
        getDisplayHierarchy(callSize);
        if (onCallSizeChange) {
            onCallSizeChange(callSize);
        }
    }, [getDisplayHierarchy, onCallSizeChange]);

    const changeFinalNode = useCallback((node: IProductNavigatorParentNode | null) => {
        setFinalNode(node);
        if (onFinalNodeChange) {
            onFinalNodeChange(node?.nodeID);
        }
        
        if(node?.filterID){
           quoteActions.SetIsUsingProductFilter(true);
        }

    }, [onFinalNodeChange, quoteActions]);

    const buildNodeMap = useCallback((nh: IProductNavigatorParentNode[]) => {
        var map = new Map<string, IProductNavigatorParentNode>();
        nh.forEach((n) => {
            map.set(n.nodeID, n);
        })
        setNodeMap(map);
        return map;
    }, []);

    const handleCallSizeChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        changeCallSize(e.target.value);
    }, [changeCallSize]);

    const getCallSizes = useCallback(async () => {
        setCallSizesLoading(true);
        return await ProductNavigatorAPI.CallSizes(oKey, partList).then((cs) => {
            setCallSizeList(cs);
            return cs;
        }).finally(() => {
            setCallSizesLoading(false);
        });
    }, [oKey, partList]);

    const getParentNodes = useCallback(async () => {
        setParentNodesLoading(true);
        return await ProductNavigatorAPI.ParentNodes().then((pn) => {
            return buildNodeMap(pn);
        }).finally(() => {
            setParentNodesLoading(false);
        });
    }, [buildNodeMap]);

    const setInitialFinalNode = useCallback((map: Map<string, IProductNavigatorParentNode>, displayHierarchy: IProductNavigatorNode[]) => {
        let finalNodeToStart: IProductNavigatorParentNode | undefined;

        if (!isNullOrWhiteSpace(initialPartNo) && !isNullOrWhiteSpace(initialPartNoSuffix)) {
            var targetNode = displayHierarchy.find((n) => CaseInsensitiveCompare(n.masterPartNo, initialPartNo!) && CaseInsensitiveCompare(n.masterPartNoSuffix, initialPartNoSuffix!));
            if (targetNode) {
                finalNodeToStart = map.get(targetNode.parentNodeID);
            }
        } else {
            finalNodeToStart = map.get(initialFinalNodeID)
        }

        if (finalNodeToStart) {
            changeFinalNode(finalNodeToStart);
        }
    }, [initialPartNo, initialPartNoSuffix, initialFinalNodeID, changeFinalNode]);

    useEffect(() => {
        if (oKey !== null && !initialLoadingStarted) {
            setInitialLoadingStarted(true);
            setCallSize(initialCallSize);
            Promise.all([getCallSizes(), getParentNodes(), getDisplayHierarchy(initialCallSize)]).then(([_, map, dh]) => {
                setInitialFinalNode(map, dh);
            });
        }
    }, [oKey, initialCallSize, initialLoadingStarted, getCallSizes, getParentNodes, getDisplayHierarchy, setInitialFinalNode]);

    useEffect(() => {
        let d = Array<IProductNavigatorNode>();
        if (displayHierarchy) {
            displayHierarchy.forEach((n) => {
                if (n.parentNodeID === (finalNode?.nodeID ?? "")) {
                    d.push(n);
                }
            })
        }
        var same = d.length === displayNodes.length;
        if (same) {
            d.forEach((d, i) => {same = (same && d.nodeID === displayNodes[i].nodeID)})
        }
        if (!same) {
            setDisplayNodes([]);
            const timer = setTimeout(() => {
                setDisplayNodes(d);
            }, Constants.TransitionDelays.Collapse);
            return (() => clearTimeout(timer));
        }

    }, [finalNode, displayHierarchy]); // eslint-disable-line react-hooks/exhaustive-deps

    return <> 
        <Container maxWidth="xl">
            <Box p={1} gap={1}>
                <Grid container direction="column" justifyItems="stretch">
                    <Grid item>
                        <Stack direction="row" justifyContent="space-between">
                            <ProductNavigatorBreadCrumbs partList={partList} nodeMap={nodeMap} finalNode={finalNode} onCancel={onCancel} setFinalNode={changeFinalNode}/>
                            {(finalNode === null || finalNode?.filterID === null) &&
                            <TextField  size="small" style={{minWidth: 125, maxWidth: 125, marginLeft: 3}} label={tm.Get("Call Size")} value={callSize} onChange={handleCallSizeChange} select>
                                {callSizeList.map((cs) => {
                                    return <MenuItem key={cs} value={cs}>{cs === "" ? "{" + tm.Get("All") + "}" : cs}</MenuItem>
                                })}
                            </TextField>
}
                        </Stack>
                    </Grid>
                </Grid>
                <TransitionGroup style={isMobile ? {display: "flex", flexWrap: "wrap", justifyContent:"center"} : {}}>
                    {displayNodes.map((n) => {
                        return <Collapse key={n.nodeID} style={{display: "inline-block"}}>
                            <ProductNavigatorNodeDisplay node={n} nodeMap={nodeMap} setFinalNode={changeFinalNode} onPartSelected={onPartSelected} />
                        </Collapse>
                    })}
                </TransitionGroup>
                {finalNode?.filterID && 
                <ProductNavigatorPartFilter node={finalNode}/>
                }
            </Box>
        </Container>
    </>
}

export default ProductNavigator;